import { EzException } from '/ezlibrary/exceptions/EzException.js';

import {
    EzObject,
    EzNumber,
    EzString
} from '/ezlibrary/helpers/EzHelpers.js';

/**
 * @class
 * @extends {EzException}
 * @description
 * Represents an exception thrown when a spinner is not found.
 * @example
 * throw new EzSpinnerNotFoundException(
 *  'Spinner with ID "spinner1" not found.',
 *  {
 *      spinnerId: 'spinner1',
 *      spinnerContainerId: 'container1'
 *  });
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Import with:
 *     import { EzSpinnerNotFoundException } from '/public/webcomponents/spinner/EzSpinnerNotFoundException.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 */
export class EzSpinnerNotFoundException extends EzException {
    /**
     * @static
     * @public @readonly @property
     * Gets the spinnerId not provided constant string: '(spinnerId not provided)'
     * @returns {string}
     */
    static get sConst_spinnerIdNotProvided() {
        return '(spinnerId not provided)';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the spinnerContainerId not provided constant string: '(spinnerContainerId not provided)'
     * @returns {string}
     */
    static get sConst_spinnerContainerIdNotProvided() {
        return '(spinnerContainerId not provided)';
    }

    static get sConst_spinnerDoesNotOrNoLongerExistsMsg() {
        return 'Spinner does not (or no longer) exists.';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the default EzSpinnerNotFoundException options object:
     *  {
     *      name: 'EzSpinnerNotFoundException',
     *      errorCode: {number},
     *      message: {null|object}
     *      causedBy: {null|object}
     *      spinnerId: 'string',
     *      spinnerContainerId: 'string',
     *      inSourceFileName: '(filename not proided)',
     *      atSourceLineNumber: '(line number not provided)',
     *      stack: '(stack not provided'
     *  }
     * @type {object}
     */
    static get sConst_defaultSpinnerNotFoundExceptionOptions() {
        let defaultSpinnerOptions = EzException.DEFAULT_EXCEPTION_OPTIONS;

        defaultSpinnerOptions.name = EzString.stringWithTextOrDefault(
            EzSpinnerNotFoundException?.constructor?.name,
            'EzSpinnerNotFoundException');

        defaultSpinnerOptions.errorCode = 404;

        defaultSpinnerOptions.message = EzSpinnerNotFoundException.sConst_spinnerDoesNotOrNoLongerExistsMsg;

        defaultSpinnerOptions.spinnerId = EzSpinnerNotFoundException.sConst_spinnerIdNotProvided;

        defaultSpinnerOptions.spinnerContainerId = EzSpinnerNotFoundException.sConst_spinnerContainerIdNotProvided;

        return defaultSpinnerOptions;
    }

    /**
     * @static
     * @public @function
     * Builds the default spinner not found exception options object.
     * @param {number} [errorCode=404]
     * The error code for the exception.
     * @param {string} [message=EzString.EMPTY]
     * The error message for the exception.
     * @param {object} [causedBy=null]
     * The object that caused the exception.
     * @param {string} [spinnerId=EzSpinnerNotFoundException.sConst_spinnerIdNotProvided]
     * The spinner ID that was not found.
     * @param {string} [spinnerContainerId=EzSpinnerNotFoundException.sConst_spinnerContainerIdNotProvided]
     * The spinner container ID that was not found.
     * @param {string} [inSourceFileName='(filename not proided)']
     * The source file name where the exception was thrown.
     * @param {string|number} [atSourceLineNumber='(line number not provided)']
     * The source line number where the exception was thrown.
     * @param {string} [stack='(stack not provided)']
     * The stack trace of the exception.
     * @returns {object}
     * The built spinner not found exception options object.
     * Example options object:
     *  {
     *      name: {string: EzSpinnerNotFoundException?.constructor?.name|'EzSpinnerNotFoundException'},
     *      errorCode: {number: errorCode|404},
     *      causedBy: {object: causedBy|null}
     *      inSourceFileName: {string: inSourceFileName|{EzException.sConst_soruceFileNameNotProvided}
     *      atSourceLineNumber: {number|string: atSourceLineNumber|EzException.sConst_soruceFileNameNotProvided}
     *      stack: {object: stack|EzEception.oConst_defaultStackTrace}
     *      message: '{string: message|EzSpinnerNotFoundException.buildSpinnerNotFoundMessage(spinnerNotFoundExOptions)}'
     *      spinnerId: 'string: spinnerId|EzSpinnerNotFoundException.sConst_spinnerIdNotProvided}'
     *      spinnerContainerId: 'string: spinnerContainerId|EzSpinnerNotFoundException.sConst_spinnerContainerIdNotProvided}'
     *  }
     *
     */
    static buildSpinnerNotFoundExceptionOptions(errorCode, message = EzString.EMPTY, spinnerId = EzSpinnerNotFoundException.sConst_spinnerIdNotProvided,
        spinnerContainerId = EzSpinnerNotFoundException.sConst_spinnerContainerIdNotProvided, causedBy = null,
        inSourceFileName = EzException.sConst_soruceFileNameNotProvided, atSourceLineNumber = EzException.sConst_soruceFileNameNotProvided,
        stack = EzException.oConst_defaultStackTrace) {
        let spinnerNotFoundExOptions = EzSpinnerNotFoundException.sConst_defaultSpinnerNotFoundExceptionOptions;

        if (EzNumber.isNumber(errorCode)) {
            spinnerNotFoundExOptions.errorCode = errorCode;
        }

        spinnerNotFoundExOptions.spinnerId = EzString.stringWithTextOrDefault(
            spinnerId,
            EzSpinnerNotFoundException.sConst_spinnerIdNotProvided);

        spinnerNotFoundExOptions.spinnerContainerId = EzString.stringWithTextOrDefault(
            spinnerContainerId,
            EzSpinnerNotFoundException.sConst_spinnerIdNotProvided);

        spinnerNotFoundExOptions.causedBy = EzObject.assignOrNull(causedBy);

        if (EzString.hasText(inSourceFileName)) {
            spinnerNotFoundExOptions.inSourceFileName = inSourceFileName;
        }

        if (EzString.hasText(atSourceLineNumber)) {
            spinnerNotFoundExOptions.atSourceLineNumber = atSourceLineNumber;
        }

        if (EzObject.isValid(stack)) {
            spinnerNotFoundExOptions.stack = stack;
        }

        spinnerNotFoundExOptions.message = EzString.stringWithTextOrDefault(
            message,
            EzSpinnerNotFoundException.buildSpinnerNotFoundMessage(spinnerNotFoundExOptions));

        return spinnerNotFoundExOptions;
    }

    /**
     * @static
     * @public @function
     * Builds the spinner not found error message with template:
     * `Spinner with spinnerId={exOptions.spinnerId|EzSpinnerNotFoundException.sConst_spinnerIdNotProvided} (spinnerContainerId={exOptions.spinnerContainerId||ExSpinnerNotFoundException.sConst_spinnerContainerIdNotProvided}) does not (or no longer) exists.`
     * @param {object} [exOptions=EzSpinnerNotFoundException.sConst_defaultSpinnerNotFoundExceptionOptions]
     * The options for the exception. Example options object:
     *
     */
    static buildSpinnerNotFoundMessage(exOptions = EzSpinnerNotFoundException.sConst_defaultSpinnerNotFoundExceptionOptions) {
        return EzString.em`
            Spinner with
            spinnerId=${EzString.stringWithTextOrDefault(exOptions.spinnerId, EzSpinnerNotFoundException, sConst_spinnerIdNotProvided)}
            (spinnerContainerId=
            ${EzString.stringWithTextOrDefault(exOptions.spinnerContainerId, EzSpinnerNotFoundException.sConst_spinnerContainerIdNotProvided)})
            does not (or no longer) exists.`
    }

    /**
     * @public
     * @constructor
     * Creates an instance of EzSpinnerNotFoundException.
     * @param {string} [errorMessage=EzString.Empty]
     * The error message for the exception.
     * @param {object} [options=EzException.DEFAULT_EXCEPTION_OPTIONS]
     * The options for the exception. Example options object:
     * {
     *      errorCode: {number},
     *      cause: {null|object}
     *      spinnerId: 'string',
     *      spinnerContainerId: 'string'
     *  }
     */
    constructor(errorMessage = EzString.Empty, exOptions = EzSpinnerNotFoundException.sConst_defaultSpinnerNotFoundExceptionOptions) {
        super(
            EzString.stringWithTextOrDefault(
                errorMessage,
                EzSpinnerNotFoundException.buildSpinnerNotFoundMessage(exOptions)));
    }
}